import type { JSONSchema, ServiceSchema } from "@models/mappingConfig";

/**
 * Reading the service schema the mapping API serves (pydantic
 * `model_json_schema()` plus `x-*` keys). Nothing here knows a service or a
 * field: every decision comes from JSON Schema types/constraints and `x-*`.
 */

export type FieldKind =
  | "collection" // array of objects with x-key: CollectionTable, edited as $ops
  | "object" // sub-model with properties: nested fieldset, deep-merged
  | "prompt" // x-format prompt on a string: large textarea
  | "promptMap" // x-format prompt on a string map: one textarea per key
  | "json" // x-format json, or anything the form cannot render natively
  | "enum"
  | "enumArray" // array whose items are an enum: multi-select
  | "stringArray" // array of free strings: tags input
  | "boolean"
  | "integer"
  | "number"
  | "string";

export interface FieldDescriptor {
  name: string;
  /** Dotted path from the body root, e.g. "retrieval.ann_top_k". */
  path: string;
  /** Resolved schema: `$ref` followed, the nullable `anyOf` unwrapped. */
  schema: JSONSchema;
  kind: FieldKind;
  nullable: boolean;
  required: boolean;
  owner: "data" | "code";
  group: string;
  label: string;
  description?: string;
  hasDefault: boolean;
  /** Item key of a collection (x-key). */
  collectionKey?: string;
  /** Resolved item schema of an array field. */
  itemSchema?: JSONSchema;
  /** Sub-fields of an object field, in schema order. */
  children?: FieldDescriptor[];
}

export const DEFAULT_GROUP = "other";

const refName = (ref: string): string | null => {
  const match = /^#\/(?:\$defs|definitions)\/(.+)$/.exec(ref);
  return match ? decodeURIComponent(match[1]) : null;
};

/** Follows `$ref` (and a single-entry `allOf`, older pydantic) against `root`; sibling keys win. */
export const resolveSchema = (schema: JSONSchema, root: JSONSchema, depth = 0): JSONSchema => {
  if (depth > 32) return schema;
  if (schema.$ref) {
    const name = refName(schema.$ref);
    const target = name ? (root.$defs?.[name] ?? (root.definitions as Record<string, JSONSchema> | undefined)?.[name]) : undefined;
    if (target) {
      const { $ref: _ref, ...siblings } = schema;
      void _ref;
      return resolveSchema({ ...target, ...siblings }, root, depth + 1);
    }
  }
  if (schema.allOf?.length === 1) {
    const { allOf, ...siblings } = schema;
    return resolveSchema({ ...allOf[0], ...siblings }, root, depth + 1);
  }
  return schema;
};

const isNullSchema = (s: JSONSchema): boolean => s.type === "null";

/** `anyOf: [X, {type: null}]` (pydantic's `X | None`) → X plus `nullable`. */
export const unwrapNullable = (schema: JSONSchema, root: JSONSchema): { schema: JSONSchema; nullable: boolean } => {
  const resolved = resolveSchema(schema, root);
  const variants = resolved.anyOf ?? resolved.oneOf;
  if (variants) {
    const nonNull = variants.filter((v) => !isNullSchema(v));
    const nullable = nonNull.length < variants.length;
    if (nonNull.length === 1) {
      const { anyOf: _a, oneOf: _o, ...outer } = resolved;
      void _a;
      void _o;
      return { schema: { ...resolveSchema(nonNull[0], root), ...outer }, nullable };
    }
    return { schema: resolved, nullable };
  }
  if (Array.isArray(resolved.type) && resolved.type.includes("null")) {
    const types = resolved.type.filter((t) => t !== "null");
    return { schema: { ...resolved, type: types.length === 1 ? types[0] : types }, nullable: true };
  }
  return { schema: resolved, nullable: false };
};

const typeOf = (schema: JSONSchema): string | undefined =>
  Array.isArray(schema.type) ? undefined : schema.type;

export const kindOf = (schema: JSONSchema, root: JSONSchema): FieldKind => {
  const format = schema["x-format"];
  const type = typeOf(schema);
  if (format === "json") return "json";
  if (format === "prompt") {
    if (type === "object") return "promptMap";
    return "prompt";
  }
  if (schema.enum) return "enum";
  if (type === "array") {
    const items = schema.items ? unwrapNullable(schema.items, root).schema : undefined;
    if (items && schema["x-key"] && (typeOf(items) === "object" || items.properties)) return "collection";
    if (items?.enum) return "enumArray";
    if (items && typeOf(items) === "string") return "stringArray";
    return "json";
  }
  if (type === "object" || schema.properties) {
    return schema.properties && Object.keys(schema.properties).length > 0 ? "object" : "json";
  }
  if (type === "boolean") return "boolean";
  if (type === "integer") return "integer";
  if (type === "number") return "number";
  if (type === "string") return "string";
  return "json";
};

/** "llm_max_input_tokens" → "Llm max input tokens" (only when the schema has no title). */
export const humanize = (name: string): string => {
  const words = name.replace(/[_-]+/g, " ").trim();
  return words ? words[0].toUpperCase() + words.slice(1) : name;
};

const describe = (
  name: string,
  raw: JSONSchema,
  root: JSONSchema,
  parent: { path: string; owner: "data" | "code"; group: string; required: string[] },
  depth = 0,
): FieldDescriptor => {
  const { schema, nullable } = unwrapNullable(raw, root);
  const kind = kindOf(schema, root);
  const owner = (raw["x-owner"] ?? schema["x-owner"] ?? parent.owner) as "data" | "code";
  const group = (raw["x-group"] ?? schema["x-group"] ?? parent.group) as string;
  const path = parent.path ? `${parent.path}.${name}` : name;
  const descriptor: FieldDescriptor = {
    name,
    path,
    schema,
    kind,
    nullable,
    required: parent.required.includes(name),
    owner,
    group,
    label: schema.title ?? raw.title ?? humanize(name),
    description: schema.description ?? raw.description,
    hasDefault: "default" in raw || "default" in schema,
  };
  if (kind === "collection" || kind === "enumArray" || kind === "stringArray") {
    descriptor.itemSchema = schema.items ? unwrapNullable(schema.items, root).schema : undefined;
    if (kind === "collection") descriptor.collectionKey = schema["x-key"];
  }
  if (kind === "object" && depth < 8) {
    descriptor.children = Object.entries(schema.properties ?? {}).map(([childName, child]) =>
      describe(childName, child, root, { path, owner, group, required: schema.required ?? [] }, depth + 1),
    );
  }
  return descriptor;
};

/** Top-level body fields, in schema order. */
export const describeFields = (jsonSchema: JSONSchema): FieldDescriptor[] =>
  Object.entries(jsonSchema.properties ?? {}).map(([name, prop]) =>
    describe(name, prop, jsonSchema, { path: "", owner: "data", group: DEFAULT_GROUP, required: jsonSchema.required ?? [] }),
  );

/** Columns of a collection: the item model's properties, in schema order. */
export const describeItemFields = (itemSchema: JSONSchema | undefined, root: JSONSchema): FieldDescriptor[] =>
  Object.entries(itemSchema?.properties ?? {}).map(([name, prop]) =>
    describe(name, prop, root, { path: "", owner: "data", group: DEFAULT_GROUP, required: itemSchema?.required ?? [] }),
  );

export interface FieldGroup {
  group: string;
  fields: FieldDescriptor[];
}

/** Sections in `groups` order; a group the server did not list comes after, in first-seen order. */
export const groupFields = (schema: ServiceSchema): FieldGroup[] => {
  // Code-owned fields (the prompts) are read from code at runtime: not
  // configurable here, so never shown. The API leaves them out too.
  const fields = describeFields(schema.json_schema).filter((f) => f.owner !== "code");
  const order = [...schema.groups];
  for (const field of fields) if (!order.includes(field.group)) order.push(field.group);
  return order
    .map((group) => ({ group, fields: fields.filter((f) => f.group === group) }))
    .filter((g) => g.fields.length > 0);
};

/** Field values of a version: the schema's properties only, never the version envelope. */
export const bodyOf = (version: Record<string, unknown>, jsonSchema: JSONSchema): Record<string, unknown> => {
  const body: Record<string, unknown> = {};
  for (const name of Object.keys(jsonSchema.properties ?? {})) {
    if (name in version) body[name] = version[name];
  }
  return body;
};

/**
 * Which form groups (tabs) hold an edit and which hold a problem, so a tabbed
 * form can flag the tabs the user cannot currently see.
 */
export const groupStatus = (
  groups: FieldGroup[],
  value: Record<string, unknown>,
  original: Record<string, unknown> | undefined,
  problemPaths: string[],
): { changed: Set<string>; problems: Set<string> } => {
  const groupOf = new Map<string, string>();
  for (const { group, fields } of groups) for (const f of fields) groupOf.set(f.name, group);
  const changed = new Set<string>();
  if (original) {
    for (const [name, group] of groupOf) {
      if (JSON.stringify(value[name]) !== JSON.stringify(original[name])) changed.add(group);
    }
  }
  const problems = new Set<string>();
  for (const path of problemPaths) {
    const group = groupOf.get(path.split(/[.[]/, 1)[0]);
    if (group) problems.add(group);
  }
  return { changed, problems };
};
